"use client";

import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import DebugPanel from "@/components/DebugPanel";

function safeCallbackPath(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return null;
  return raw;
}

function LoginContent() {
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  // Where sign-in lands: the path the gate/401 bounced from, else the trip
  // planner. Only a same-origin PATH is honored — an absolute URL or a
  // protocol-relative "//host" in the query is dropped (open redirect).
  const callbackUrl = safeCallbackPath(searchParams.get("callbackUrl")) ?? "/trip";
  const [showDebug, setShowDebug] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6">
      <div className="w-full max-w-sm space-y-8 text-center">
        <div>
          <h1 className="text-3xl font-bold">Hu Family Apps</h1>
          <p className="mt-2" style={{ color: "var(--text3)" }}>Sign in to continue</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">
            {error === "AccessDenied"
              ? "Sorry, your Google account is not on the allowlist."
              : "Something went wrong. Please try again."}
          </div>
        )}

        <button
          onClick={() => signIn("google", { callbackUrl })}
          className="w-full flex items-center justify-center gap-3 rounded-lg border px-6 py-3 font-medium shadow-sm transition hover:-translate-y-0.5"
          style={{ background: "var(--card)", borderColor: "var(--border)", color: "var(--text2)" }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </button>

        {/* Recovery tools, reachable signed out: a deployed fix (new SW,
            new build) sometimes has to land BEFORE sign-in can work. */}
        <div className="pt-4">
          <button
            onClick={() => setShowDebug((v) => !v)}
            className="text-sm underline-offset-2 hover:underline"
            style={{ color: "var(--text3)" }}
          >
            {showDebug ? "Hide debug" : "Debug"}
          </button>
        </div>
      </div>
      {showDebug && (
        <div className="mt-6 w-full max-w-lg text-left">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text3)" }}>
            Debug · app update & reset
          </div>
          <DebugPanel variant="recovery" />
        </div>
      )}
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}
